// @ts-nocheck
'use client';

import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PlusIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import _ from 'lodash';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import { getFormattedMissingFieldsMessage } from 'utils';
import SelectComponent from 'components/common/SelectComponent';
import ButtonComponent from 'components/common/ButtonComponent';
import BlockTable from 'components/common/BlockTable';
import BlockFormModal from 'components/modals/BlockFormModal';
import TunnelRowModal from 'components/modals/TunnelRowModal';
import ActionCardComponent from 'components/common/ActionCardComponent';
import UploadFarmingBlockModal from 'components/modals/UploadFarmingBlockModal';

import { useAuth } from 'auth/AuthContext';
import { useTranslation } from 'react-i18next';

const FarmingBlocksManagement = ({ blockGroups, _onRefreshBlockGroups }) => {
  const { t } = useTranslation();
  const tunnelRefreshRef = useRef(null);
  const { user } = useAuth();
  const [state, setState] = useState({
    blocks: [],
    error: null,
    selectedBlock: {},
    selectedBlockGroupId: '',
    filter: '',
    isOpenBlockFormModal: false,
    searchTerm: '',
    isTunnelRowModalOpen: false,
    selectedTunnelRowIndex: null,
    selectedTunnelRowData: {
      rowId: '',
      cropId: '',
      varietyId: '',
      numberOfPlants: '',
      subA: null,
      subB: null
    },
    varieties: [],
    isOpenUploadFarmingBlockModal: false
  });

  // Load farming blocks
  const getFarmingBlocks = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/${user.farmId}?filter=${state.filter}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });

      setState(s => ({
        ...s,
        blocks: Array.isArray(response.data) ? response.data : [],
        error: null
      }));
    } catch (err) {
      showAlert({ message: t('blocks.failedToLoadBlocks'), icon: 'error' });
      setState(s => ({
        ...s,
        error: t('blocks.failedToLoadBlocks')
      }));
    }
  };

  const deleteFarmingBlocks = async blockId => {
    try {
      await makeRequest({
        method: 'DELETE',
        url: `/management/farming-blocks/${blockId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        alertResult: true
      });
      await getFarmingBlocks();
    } catch (err) {
      showAlert({ message: t('blocks.failedToDeleteBlock'), icon: 'error' });
      setState(s => ({
        ...s,
        error: t('blocks.failedToDeleteBlock')
      }));
    }
  };

  const putPostFarmingBlocks = async formData => {
    const requiredFields = ['name', 'cropId', 'varietyId', 'area'];
    const htmlMessage = getFormattedMissingFieldsMessage(formData, requiredFields);
    if (htmlMessage) {
      showAlert({ html: htmlMessage, icon: 'error' });
      return;
    }

    const isEdit = !!state.selectedBlock?.farmingBlockId;
    const url = isEdit
      ? `/management/farming-blocks/${user.farmId}/update/${state.selectedBlock.farmingBlockId}`
      : `/management/farming-blocks/${user.farmId}`;
    const method = isEdit ? 'PUT' : 'POST';

    // keep blockGroupId ONLY if it's a finite number (coerces strings)
    const { blockGroupId, ...rest } = formData;
    const num = Number(blockGroupId);
    const data = Number.isFinite(num) ? { ...rest, blockGroupId: num } : rest;

    try {
      await makeRequest({
        method,
        url,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'application/json'
        },
        data,
        alertResult: true
      });

      getFarmingBlocks();
      setState(s => ({ ...s, selectedBlock: {} }));
      onCloseBlockFormModal();
    } catch (err) {
      showAlert({ message: t('blocks.failedToSaveBlock'), icon: 'error' });
      setState(s => ({ ...s, error: t('blocks.failedToSaveBlock'), selectedBlock: {} }));
    }
  };

  const getFarmingBlocksVarieties = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/crops/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });
      setState(s => ({
        ...s,
        varieties: Array.isArray(response.data) ? response.data : []
      }));
    } catch (err) {
      showAlert({ message: t('blocks.failedToLoadCropsAndVarieties'), icon: 'error' });
      setState(s => ({
        ...s,
        error: t('blocks.failedToLoadCropsAndVarieties'),
        varieties: []
      }));
    }
  };

  const onOpenBlockFormModal = () => {
    setState(s => ({
      ...s,
      selectedBlock: {},
      isOpenBlockFormModal: true
    }));
  };

  const onEditBlock = block => {
    setState(s => ({
      ...s,
      selectedBlock: _.cloneDeep(block),
      isOpenBlockFormModal: true
    }));
  };

  const onCloseBlockFormModal = () => {
    setState(s => ({
      ...s,
      selectedBlock: {},
      isOpenBlockFormModal: false
    }));
  };

  const onFilterBlockGroupsList = e => {
    setState(s => ({
      ...s,
      selectedBlockGroupId: e.target.value
    }));
  };

  const toggleIsTunnelRowModalOpen = (index = null, initialData = {}) => {
    setState(s => ({
      ...s,
      isTunnelRowModalOpen: !s.isTunnelRowModalOpen,
      selectedTunnelRowIndex: index !== null ? index : s.selectedTunnelRowIndex,
      selectedTunnelRowData: initialData
    }));
  };

  const onToggleUploadCsvModal = () => {
    setState(s => ({
      ...s,
      isOpenUploadFarmingBlockModal: !s.isOpenUploadFarmingBlockModal
    }));
  };

  const filteredBlocks = state.blocks.filter(block => {
    // Filter by block group if selected
    const matchesGroup =
      !state.selectedBlockGroupId || block.blockGroupId === parseInt(state.selectedBlockGroupId);

    // Filter by search term if provided
    const matchesSearch =
      !state.searchTerm ||
      block.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      (block.cropId &&
        String(block.cropId).toLowerCase().includes(state.searchTerm.toLowerCase())) ||
      (block.varietyId &&
        String(block.varietyId).toLowerCase().includes(state.searchTerm.toLowerCase()));

    return matchesGroup && matchesSearch;
  });

  useEffect(() => {
    getFarmingBlocks();
    getFarmingBlocksVarieties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (blockGroups.length > 0) {
      // Refresh blocks when block groups change (e.g., after creating/deleting a group)
      getFarmingBlocks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockGroups]);

  return (
    <>
      {!!user.farmId && (
        <ActionCardComponent>
          <input
            type="text"
            value={state.searchTerm}
            placeholder={t('blocks.filterFarmingBlocks')}
            onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
            className="search-input"
            data-align="left"
          />

          <SelectComponent
            data-align="left"
            id="blockGroupId"
            name="blockGroupId"
            value={state.selectedBlockGroupId}
            onChange={onFilterBlockGroupsList}
          >
            <option value="">{t('blocks.allFarmsInGroups')}</option>
            {blockGroups.map(group => (
              <option key={group.blockGroupId} value={group.blockGroupId}>
                {group.groupName}
              </option>
            ))}
          </SelectComponent>

          <ButtonComponent
            onClick={() => setState(s => ({ ...s, isOpenUploadFarmingBlockModal: true }))}
          >
            <DocumentArrowUpIcon className="w-5 h-5" />
            <span className="font-medium">{t('blocks.importFarmingBlock')}</span>
          </ButtonComponent>

          <ButtonComponent onClick={onOpenBlockFormModal}>
            <PlusIcon className="w-5 h-5" />
            <span className="font-medium">{t('blocks.newBlock')}</span>
          </ButtonComponent>
        </ActionCardComponent>
      )}

      {/* farming blocks table */}
      {state.blocks.length !== 0 && (
        <BlockTable
          onEdit={onEditBlock}
          onDelete={deleteFarmingBlocks}
          blockGroups={blockGroups}
          filteredBlocks={filteredBlocks}
          toggleIsTunnelRowModalOpen={toggleIsTunnelRowModalOpen}
          tunnelRefreshRef={tunnelRefreshRef}
        />
      )}

      {/* block form modal */}
      <BlockFormModal
        isOpen={state.isOpenBlockFormModal}
        selectedBlock={state.selectedBlock}
        blockGroups={blockGroups}
        varietiesOptions={state.varieties}
        onClose={onCloseBlockFormModal}
        onSave={putPostFarmingBlocks}
      />

      <TunnelRowModal
        isOpen={state.isTunnelRowModalOpen}
        rowIndex={state.selectedTunnelRowIndex}
        onClose={toggleIsTunnelRowModalOpen}
        initialData={state.selectedTunnelRowData}
        varietiesOptions={state.varieties}
        onRefresh={() => {
          if (tunnelRefreshRef.current) {
            tunnelRefreshRef.current();
          }
        }}
      />
      <UploadFarmingBlockModal
        isOpen={state.isOpenUploadFarmingBlockModal}
        onClose={onToggleUploadCsvModal}
      />
    </>
  );
};

FarmingBlocksManagement.propTypes = {
  blockGroups: PropTypes.arrayOf(PropTypes.object),
  _onRefreshBlockGroups: PropTypes.func
};

export default FarmingBlocksManagement;
